/**
 * ContractsPanel — customer contracts (fee agreements, vendor/TMC, hotel) with
 * optional document upload to Azure Blob.
 *
 *   list   GET    /api/customers/[id]/contracts
 *   add    POST   /api/customers/[id]/contracts        { contract_type, … , doc_blob_path? }
 *   remove DELETE /api/customers/[id]/contracts?row_id=
 *   upload POST   /api/customers/[id]/contracts/upload  → { upload_url, blob_path }
 *          then  PUT bytes → upload_url  (file never touches our server)
 *   open   GET    /api/customers/[id]/contracts/[rowId]/download → { url }
 *
 * Edits require edit:customer_settings; viewers get a read-only list.
 */

import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';

interface Contract {
  id: string;
  contractType: string;
  vendor: string | null;
  feeTerms: string | null;
  startDate: string | null;
  endDate: string | null;
  docBlobPath: string | null;
  notes: string | null;
  createdBy: string;
}

const CONTRACT_TYPES = [
  'Fee Agreement',
  'Vendor',
  'Hotel',
  'TMC',
  'Other',
] as const;

export function ContractsPanel({ customerId }: { customerId: string }) {
  const { can } = useMe();
  const canEdit = can('edit:customer_settings');
  // Removing a contract is destructive and stays with admin+manager, while ADDING
  // one is open to every internal role (2026-08-11). Rendering a Remove button
  // for someone the server will 403 is worse than hiding it — they click a
  // confirm dialog and only then find out.
  const canDelete = can('delete:customer_record');
  const enc = encodeURIComponent(customerId);

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const d = await apiFetch<{ contracts: Contract[] }>(
        `/api/customers/${enc}/contracts`,
      );
      setContracts(d.contracts);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const remove = async (id: string) => {
    if (!confirm('Remove this contract?')) return;
    try {
      await apiFetch(`/api/customers/${enc}/contracts?row_id=${id}`, {
        method: 'DELETE',
      });
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  };

  const openDoc = async (id: string) => {
    // Open the tab synchronously inside the click gesture so the popup
    // blocker doesn't kill it, then point it at the signed URL once minted.
    const tab = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const { url } = await apiFetch<{ url: string }>(
        `/api/customers/${enc}/contracts/${id}/download`,
      );
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      tab?.close();
      alert(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <span className="section-label">Contracts ({contracts.length})</span>
        {canEdit && !adding && (
          <button
            className="btn btn-outline btn-sm"
            onClick={() => setAdding(true)}
          >
            + Add contract
          </button>
        )}
      </div>
      <div className="section-body">
        {error && (
          <div className="banner banner-error" style={{ marginBottom: 10 }}>
            {error}
          </div>
        )}

        {adding && canEdit && (
          <ContractEditor
            customerId={customerId}
            onCancel={() => setAdding(false)}
            onSaved={() => {
              setAdding(false);
              load();
            }}
          />
        )}

        {loading && (
          <div
            style={{ fontSize: 'var(--fs-base)', color: 'var(--text-muted)' }}
          >
            Loading contracts…
          </div>
        )}

        {!loading && contracts.length === 0 && !adding && (
          <div
            style={{
              fontSize: 'var(--fs-base)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            No contracts on file. {canEdit && 'Add one above.'}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {contracts.map((c) => (
            <div
              key={c.id}
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg-card)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>
                    {c.contractType}
                  </span>
                  {c.vendor && (
                    <span
                      style={{
                        fontSize: 'var(--fs-base)',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      · {c.vendor}
                    </span>
                  )}
                  {c.docBlobPath && (
                    <button
                      className="link"
                      style={{
                        fontSize: 'var(--fs-sm)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                      onClick={() => openDoc(c.id)}
                    >
                      📎 View document
                    </button>
                  )}
                </div>
                {c.feeTerms && (
                  <div
                    style={{
                      fontSize: 'var(--fs-base)',
                      color: 'var(--text-secondary)',
                      marginTop: 3,
                    }}
                  >
                    {c.feeTerms}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-muted)',
                    marginTop: 4,
                  }}
                >
                  {c.startDate || c.endDate
                    ? `${c.startDate ?? '—'} → ${c.endDate ?? '—'}`
                    : 'No term dates'}
                </div>
                {c.notes && (
                  <div
                    style={{
                      fontSize: 'var(--fs-base)',
                      color: 'var(--text-secondary)',
                      marginTop: 4,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {c.notes}
                  </div>
                )}
              </div>
              {canDelete && (
                <button
                  className="btn btn-outline btn-sm"
                  style={{ color: '#B22B2B' }}
                  onClick={() => remove(c.id)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ContractEditor({
  customerId,
  onCancel,
  onSaved,
}: {
  customerId: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const enc = encodeURIComponent(customerId);
  const [contractType, setContractType] = useState<string>(CONTRACT_TYPES[0]);
  const [vendor, setVendor] = useState('');
  const [feeTerms, setFeeTerms] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    try {
      setSaving(true);
      setErr(null);

      let docBlobPath: string | null = null;
      if (file) {
        // 1. mint a signed upload URL + blob path
        setStatus('Requesting upload URL…');
        const mint = await apiFetch<{
          upload_url: string;
          blob_path: string;
        }>(`/api/customers/${enc}/contracts/upload`, {
          method: 'POST',
          body: JSON.stringify({
            filename: file.name,
            mime_type: file.type || 'application/octet-stream',
            size_bytes: file.size,
          }),
        });
        // 2. PUT the bytes straight to Blob (no bearer; SAS authorizes).
        setStatus('Uploading document…');
        const put = await fetch(mint.upload_url, {
          method: 'PUT',
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
        });
        if (!put.ok) {
          throw new Error(`Upload failed (${put.status})`);
        }
        docBlobPath = mint.blob_path;
      }

      // 3. create the contract row (records the blob pointer)
      setStatus('Saving contract…');
      await apiFetch(`/api/customers/${enc}/contracts`, {
        method: 'POST',
        body: JSON.stringify({
          contract_type: contractType,
          vendor: vendor.trim() || null,
          fee_terms: feeTerms.trim() || null,
          start_date: startDate || null,
          end_date: endDate || null,
          notes: notes.trim() || null,
          doc_blob_path: docBlobPath,
        }),
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
      setStatus(null);
    }
  };

  return (
    <div
      style={{
        padding: 14,
        border: '2px solid var(--accent)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--bg-card)',
        boxShadow: 'var(--shadow-2)',
        marginBottom: 10,
      }}
    >
      <div className="form-grid" style={{ marginBottom: 10 }}>
        <Field label="Type">
          <select
            className="input"
            value={contractType}
            onChange={(e) => setContractType(e.target.value)}
          >
            {CONTRACT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Vendor / counterparty">
          <input
            className="input"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
          />
        </Field>
        <Field label="Start date">
          <input
            className="input"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Field>
        <Field label="End date">
          <input
            className="input"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Field>
      </div>

      <Field label="Fee terms">
        <input
          className="input"
          placeholder="e.g. $18 online / $32 full-service; net 30"
          value={feeTerms}
          onChange={(e) => setFeeTerms(e.target.value)}
        />
      </Field>

      <div style={{ marginTop: 10 }}>
        <Field label="Notes">
          <textarea
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </div>

      <div style={{ marginTop: 10 }}>
        <Field label="Document (optional, ≤50 MB)">
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
        {file && (
          <div
            style={{
              fontSize: 'var(--fs-sm)',
              color: 'var(--text-muted)',
              marginTop: 4,
            }}
          >
            {file.name} · {(file.size / 1024).toFixed(0)} KB
          </div>
        )}
      </div>

      {err && (
        <div className="banner banner-error" style={{ marginTop: 10 }}>
          {err}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: 12,
        }}
      >
        {status && (
          <span
            style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}
          >
            {status}
          </span>
        )}
        <button
          className="btn btn-outline btn-sm"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={save}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Add contract'}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 600,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
